import React from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  InformationCircleIcon 
} from '@heroicons/react/24/outline';

const SEOScoreWidget = ({ seoAnalysis }) => {
  if (!seoAnalysis) return null;

  const { totalScore, feedback, wordCount, readingTime, readability } = seoAnalysis;

  // Determine score color
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreRing = (score) => {
    if (score >= 80) return 'stroke-green-600';
    if (score >= 60) return 'stroke-yellow-600';
    if (score >= 40) return 'stroke-orange-600';
    return 'stroke-red-600';
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Analysis</h3>

      {/* Score Circle */}
      <div className="flex items-center justify-center mb-6">
        <div className="relative">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-gray-200"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - totalScore / 100)}`}
              className={getScoreRing(totalScore)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={`text-3xl font-bold ${getScoreColor(totalScore).split(' ')[0]}`}>
                {totalScore}
              </div>
              <div className="text-xs text-gray-500">out of 100</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900">{wordCount}</div>
          <div className="text-xs text-gray-500">Words</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900">{readingTime}</div>
          <div className="text-xs text-gray-500">Min Read</div>
        </div>
        <div className="text-center">
          <div className={`text-sm font-semibold capitalize ${
            readability?.color === 'green' ? 'text-green-600' :
            readability?.color === 'yellow' ? 'text-yellow-600' :
            readability?.color === 'orange' ? 'text-orange-600' :
            'text-red-600'
          }`}>
            {readability?.level || 'N/A'}
          </div>
          <div className="text-xs text-gray-500">Reading</div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {feedback.map((item, index) => (
          <div key={index} className="flex items-start gap-2 text-sm">
            {getIconForType(item.type)}
            <span className="text-gray-700 flex-1">{item.message}</span>
          </div>
        ))}
      </div>

      {/* SEO Score Explanation */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          <strong>Score Breakdown:</strong> Title (30%), Description (20%), Content (25%), Keywords (10%), Readability (15%)
        </div>
      </div>
    </div>
  );
};

export default SEOScoreWidget;
